import axios from 'axios'
import { startsWith } from 'lodash-es'
import { appUrl } from 'src/consts.js'
import axiosErrorHandler from './axiosErrorHandler.js'

// Configure some axios globals
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.withXSRFToken = true
axios.defaults.withCredentials = true

// Attempt to fix https://wsp-nz.sentry.io/issues/6868220067
axios.defaults.headers.get['Cache-Control'] = 'no-cache, no-store, must-revalidate'
axios.defaults.headers.get['Pragma'] = 'no-cache'
axios.defaults.headers.get['Expires'] = '0'

// Wire up our global axios error handler for handling all types of laravel api errors
axios.interceptors.response.use(null, axiosErrorHandler)

/**
 * Produce a full url to our api from and endpoint.
 */
export function apiUrl(uri) {
    // Dont try to prefix absolute uri's
    if (startsWith(uri, 'http')) return uri
    return `${appUrl}/api/spa/${uri}`
}

/**
 * Produce a url to one of our shallow resource routes using laravels shallow route convention.
 *
 * Example usage for a "structure" with a shallow "inspections' route:
 *      inspections (when editing)
 *      structure/1/inspections (when creating for particular structure)
 *
 * shallowResourceRoute(`structures/${structure.id}`, 'inspections', props.id)
 */
export function shallowResourceRoute(parentSegment, shallowRouteSegment, id) {
    return id ? shallowRouteSegment : `${parentSegment}/${shallowRouteSegment}`
}

/**
 * Helper functions for working with restful resources
 */
export function get(endpoint, params = [], config = {}) {
    return axios.get(apiUrl(endpoint), { ...config, params })
        .then(({ data }) => data)
}

export function post(endpoint, data, config = {}) {
    // Make sure the accept header is always included (unsure as to why this sometimes is omitted by axios)
    // On PROD the Azure WAF has a rule to reject any requests with a missing Accept header.
    config.headers = {
        ...config.headers || {},
        Accept: 'application/json',
    }

    return axios.post(apiUrl(endpoint), data, config)
        .then(({ data }) => data)
}

export function put(endpoint, data, config = {}) {
    return axios.put(apiUrl(endpoint), data, config)
        .then(({ data }) => data)
}

/**
 * Perform an insert or update based on whether an id is provided
 */
export function save(endpoint, id, data, isResourceRoute = true) {
    if (!id) return post(endpoint, data)

    // To allow laravel post endpoints to work with PUT
    if (data instanceof FormData) {
        data.append('_method', 'PUT')
    } else {
        data['_method'] = 'PUT'
    }
    return post(isResourceRoute ? `${endpoint}/${id}`: endpoint, data)
}

export function destroy(endpoint, id, data) {
    return axios.delete(apiUrl(`${endpoint}/${id}`), { data })
        .then(({ data }) => data)
}
